<template>
  <DefaultAuthLayout>
    <!-- Top Action Slot -->
    <template #topAction>
      <span class="text-gray mr-3 lg:inline">Já ativou sua conta?</span>
      <router-link to="/login" class="border border-stroke px-3 py-1 text-sm text-body transition-colors hover:bg-gray-2 lg:px-6 lg:py-2 lg:text-base">
        Fazer Login
      </router-link>
    </template>

    <!-- Header Slot -->
    <template #header>
      <h2 class="mb-2 text-2xl font-bold text-black lg:text-3xl">{{ t('views.account_activation.title') }}</h2>
      <p class="text-sm text-body lg:text-base">{{ t('views.account_activation.description') }}</p>
    </template>

    <!-- Form Slot -->
    <template #form>
      <form @submit.prevent="handleSubmit" class="space-y-4 lg:space-y-6">
        <!-- Activation Token Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm">
            {{ t('views.account_activation.label.activation_token') }}
          </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcKey />
            </div>
            <input
              type="text"
              name="activation_token"
              v-model="form.activation_token"
              :placeholder="t('views.account_activation.placeholder.activation_token')"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-4 lg:text-base" />
          </div>
          <p v-if="errors.activation_token" class="mt-1 text-xs text-danger">{{ errors.activation_token }}</p>
        </div>

        <!-- Submit Button -->
        <div class="mb-3 mt-6 lg:mb-5 lg:mt-8">
          <button
            @click="handleSubmit"
            :disabled="isLoading"
            class="flex w-full cursor-pointer justify-center border border-primary bg-primary p-3 font-medium text-white transition hover:bg-blue disabled:cursor-not-allowed disabled:opacity-50 lg:p-4">
            <div class="flex flex-row items-center text-center">
              <div v-if="isLoading" class="mr-3 animate-spin items-center">
                <svg class="h-5 w-5 text-white lg:h-7 lg:w-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span class="flex items-center justify-center text-base font-medium lg:text-lg">
                {{ isLoading ? 'Ativando...' : t('views.account_activation.action.submit') }}
              </span>
            </div>
          </button>
        </div>

        <!-- Help Section -->
        <div class="mt-4 text-center">
          <p class="mb-3 text-xs text-body lg:text-sm">
            {{ t('views.account_activation.label.activation_token_not_detected') }}
          </p>
          <router-link to="request-activation-token" class="text-sm font-medium text-blue underline hover:text-blue-2">
            {{ t('views.account_activation.action.request_activation_token') }}
          </router-link>
        </div>
      </form>
    </template>

    <!-- Additional Content Slot -->
    <template #additionalContent>
      <!-- Alternative Actions -->
      <div class="mt-8 border-t border-stroke pt-6 text-center">
        <p class="mb-4 text-sm text-body">Precisa criar uma nova conta?</p>
        <router-link
          to="/cadastro"
          class="inline-flex items-center justify-center border border-stroke px-4 py-2 text-sm font-medium text-body transition-colors hover:bg-gray-2">
          Cadastre-se Aqui
        </router-link>
      </div>
    </template>
  </DefaultAuthLayout>
</template>

<script setup lang="ts">
import DefaultAuthLayout from '@/components/Auths/DefaultAuthLayout.vue'
import { IcKey } from '@/components/Icons'
import { IAccountActivationParams } from '@/interfaces/api/user'
import AccountActivationSchema from '@/schemas/AccountActivationSchema'
import { useSuccessStore } from '@/store/success'
import { useUserStore } from '@/store/user'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ValidationError } from 'yup'

const { t } = useI18n()
const router = useRouter()
const { activateAccount } = useUserStore()

const isLoading = ref(false)

const form = ref({
  activation_token: '',
})

const errors = ref({
  activation_token: '',
})

const setErrors = (validationErrors: ValidationError) => {
  errors.value = {
    activation_token: '',
  }

  validationErrors.inner.forEach((error) => {
    if (error.path) {
      errors.value[error.path as keyof typeof errors.value] = error.message
    }
  })
}

const activateUserAccount = (values: IAccountActivationParams) => {
  isLoading.value = true

  activateAccount(values, {
    onSuccess: (response) => {
      isLoading.value = false
      useSuccessStore().setSuccess([response.message])
      router.push({ name: 'login' })
    },
    onFailure: () => {
      isLoading.value = false
    },
    onCompletion: () => {
      isLoading.value = false
    },
  })
}

const handleSubmit = () => {
  AccountActivationSchema.validate(form.value, { abortEarly: false })
    .then(() => {
      activateUserAccount(form.value)
    })
    .catch((err: ValidationError) => {
      setErrors(err)
    })
}
</script>
